<div class="messages-container" #messagesContainer>
  <!-- Empty state -->
  <div class="empty-state" *ngIf="!request?.messages || request.messages.length === 0">
    <mat-icon class="empty-icon">chat_bubble_outline</mat-icon>
    <p>{{ 'request_v2.info-dialog.messages.no_messages' | translate }}</p>
  </div>

  <!-- Messages list -->
  <div class="messages-list" *ngIf="request?.messages && request.messages.length > 0">
    <ng-container *ngFor="let message of sortedMessages; let i = index; trackBy: trackByMessageId">
      <!-- Date divider -->
      <div class="date-divider" *ngIf="i === 0 || formatMessageDate(message.sentAt) !== formatMessageDate(sortedMessages[i-1].sentAt)">
        <span class="date-text">{{ formatMessageDate(message.sentAt) }}</span>
      </div>

      <!-- Unread divider -->
      <div class="unread-divider" *ngIf="unreadDividerIndex !== null && i === unreadDividerIndex">
        <span class="unread-text">{{ 'request_v2.info-dialog.messages.unread_divider' | translate }}</span>
      </div>

      <!-- Message bubble -->
      <div class="message-wrapper" [class.admin]="isAdminMessage(message)" [class.user]="!isAdminMessage(message)">
        <!-- Avatar -->
        <a 
          class="avatar-link"
          [routerLink]="['/user-update', message.sender]"
          target="_blank"
          [matTooltip]="'request_v2.info-dialog.messages.view_profile' | translate"
          matTooltipPosition="below">
          <div class="avatar">
            <img *ngIf="message.sender.picture" [src]="message.sender.picture" alt="Avatar">
            <span *ngIf="!message.sender.picture" class="initials">
              {{ getInitials(message.sender.firstName, message.sender.lastName) }}
            </span>
          </div>
        </a>

        <div class="message-content-wrapper">
          <!-- Author name -->
          <div class="author-name">
            {{  message.sender.firstName }} {{ message.sender.lastName }}
            <span class="you-label" *ngIf="isAdminMessage(message)">
              ({{ 'request_v2.info-dialog.messages.you' | translate }})
            </span>
          </div>

          <!-- Message bubble -->
          <div class="message-bubble">
            <p class="message-text">{{ message.message }}</p>

            <!-- Attachments - only show if there are valid (non-empty) attachments -->
            <div class="message-attachments" *ngIf="getValidAttachments(message.attachments).length > 0">
              <div 
                class="message-attachment-preview" 
                *ngFor="let attachment of getValidAttachments(message.attachments)"
                [class.image-type]="isImage(attachment)"
                [class.pdf-type]="isPdf(attachment)"
                [class.deleted-type]="isFileDeleted(attachment)"
                [class.other-type]="!isImage(attachment) && !isPdf(attachment) && !isFileDeleted(attachment)"
                (click)="!isFileDeleted(attachment) ? openDocumentViewer(attachment) : null">
                
                <!-- Image preview -->
                <img 
                  *ngIf="isImage(attachment)" 
                  [src]="attachment" 
                  alt="Attachment"
                  class="attachment-image">
                
                <!-- PDF preview -->
                <img 
                  *ngIf="isPdf(attachment)" 
                  src="assets/images/pdf-file.svg" 
                  alt="PDF"
                  class="attachment-pdf-icon">
                
                <!-- Deleted file -->
                <div *ngIf="isFileDeleted(attachment)" class="attachment-deleted">
                    <mat-icon>block</mat-icon>
                    <span class="deleted-text">{{ 'request_v2.info-dialog.messages.file_deleted' | translate }}</span>
                </div>

                <!-- Other file types -->
                <mat-icon *ngIf="!isImage(attachment) && !isPdf(attachment) && !isFileDeleted(attachment)">insert_drive_file</mat-icon>
              </div>
            </div>
          </div>

          <!-- Timestamp -->
          <div class="message-time">{{ formatMessageTime(message.sentAt) }}</div>
        </div>
      </div>
    </ng-container>
  </div>
</div>

<!-- Polling indicator - positioned above input -->
<div class="polling-indicator" *ngIf="isPolling">
  <div class="polling-bar"></div>
</div>

<!-- Message input -->
<div *ngIf="canEdit" class="message-input-container">
  <form [formGroup]="messageForm" (ngSubmit)="sendMessage()" class="input-form">
    <!-- Pending attachments -->
    <div class="pending-attachments" *ngIf="pendingAttachments.length > 0">
      <div 
        class="attachment-preview" 
        *ngFor="let attachment of pendingAttachments; let i = index"
        [class.image-preview]="attachment.type === 'image'"
        [class.pdf-preview]="attachment.type === 'pdf'"
        [class.other-preview]="attachment.type === 'other'">
        
        <!-- Image preview -->
        <img 
          *ngIf="attachment.type === 'image'" 
          [src]="attachment.url" 
          alt="Preview"
          class="preview-image">
        
        <!-- PDF preview -->
        <img 
          *ngIf="attachment.type === 'pdf'" 
          src="assets/images/pdf-file.svg" 
          alt="PDF"
          class="pdf-icon">
        
        <!-- Other file types -->
        <div *ngIf="attachment.type === 'other'" class="other-file-icon">
          <mat-icon>insert_drive_file</mat-icon>
        </div>
        
        <!-- Close button -->
        <button type="button" class="remove-btn" (click)="removeAttachment(i)">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Predefined messages strip -->
    <div class="predefined-messages-strip" *ngIf="predefinedMessages.length > 0">
      <button
        *ngFor="let pm of predefinedMessages"
        type="button"
        class="predefined-chip"
        (click)="selectPredefinedMessage(pm.text)"
        [matTooltip]="pm.text"
        matTooltipPosition="above"
        matTooltipClass="predefined-chip-tooltip">
        {{ pm.text }}
      </button>
    </div>

    <div class="input-row">
      <!-- File attachment button -->
      <input
        #fileInput
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx"
        (change)="onNativeFileSelected($event)"
        style="display: none;">
      <button 
        type="button" 
        class="attach-btn" 
        (click)="fileInput.click()" 
        [disabled]="uploading || sending"
        [matTooltip]="'request_v2.info-dialog.messages.attach_file' | translate"
        matTooltipPosition="above">
        <mat-icon>attach_file</mat-icon>
      </button>

      <!-- Text input -->
      <textarea
        formControlName="message"
        [placeholder]="'request_v2.info-dialog.messages.placeholder' | translate"
        class="message-input"
        rows="1"
        [attr.disabled]="uploading ? true : null"
        (keydown.enter)="$event.preventDefault(); sendMessage()">
      </textarea>

      <!-- Send button -->
      <button 
        type="submit" 
        class="send-btn"
        [disabled]="messageForm.invalid || sending || uploading"
        [matTooltip]="'request_v2.info-dialog.messages.send' | translate"
        matTooltipPosition="above">
        <mat-icon *ngIf="!sending && !uploading">send</mat-icon>
        <mat-spinner *ngIf="sending || uploading" diameter="20"></mat-spinner>
      </button>
    </div>
  </form>
</div>
